import WarehouseRowEditor, { relocationHeaderStyles } from '../WarehouseRowEditor';
import { CustomField } from 'src/components/molecules/formik/CustomField';
import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/components/atoms/WarehouseSelector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { Alert, Box, Button, Checkbox, InputAdornment, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PrintIcon from '@mui/icons-material/Print';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { generateTablePageButtons } from 'src/utils/generateTablePageButtons';
import { useGetWarehouseLocationsById } from 'src/hooks/api/requestConfigs/warehouse';
import useRequest from 'src/hooks/api/useRequest';
import { useSettingsContext } from 'src/hooks/useSettings';
import { buildProductRelocation, buildProductSearchIndex, findMatchingProductIds, getProductLocationAvailable, useProductLocationsTable } from 'src/hooks/table/configs/useProductLocationsTable';
import type { ProductLocationRow, RelocationLocation, RelocationValues } from 'src/hooks/table/configs/useProductLocationsTable';
import { printRelocationOrder, relocateProduct } from 'src/utils/apiRequests';
import { printDispatchPdf, useDispatchPrintingSettings } from 'src/components/organisms/printing/PrintingManager';
import type { DispatchPrintingSettings, PrintOutcome } from 'src/components/organisms/printing/PrintingManager';

type Identifier = string | number;
type Notice = { severity: 'success' | 'error' | 'info'; message: string };
const productFilterId = 'relocate-selected-product';
const emptyValues: RelocationValues = { Quantity: '', ToLocationId: '' };

type RelocationOrder = { orderId: Identifier; warehouseId: Identifier };
type RelocationPrintResult = { outcome?: PrintOutcome; error?: string };
type RelocationPrintJob = RelocationOrder & { key: number; settings: DispatchPrintingSettings };
const hasIdentifier = (value: unknown): value is Identifier => (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value));

// Keep this hook on the page, above stations that remount after a save.
export const useRelocationOrderPrinting = (page: 'relocateProducts' | 'relocateReceivedProducts') => {
    const { settings, setSetting } = useSettingsContext();
    const printingSettings = useDispatchPrintingSettings();
    const autoPrintKey = `${page}AutoPrintRelocationOrder`;
    const lastOrderKey = `${page}LastRelocationOrder`;
    const autoPrint = settings?.[autoPrintKey] === true || settings?.[autoPrintKey] === 'true';
    const storedOrder = settings?.[lastOrderKey];
    const lastOrder: RelocationOrder | null = hasIdentifier(storedOrder?.orderId) && hasIdentifier(storedOrder?.warehouseId) ? storedOrder : null;
    const [jobs, setJobs] = useState<RelocationPrintJob[]>([]);
    const [notice, setNotice] = useState<{ severity: 'warning' | 'error'; message: string } | null>(null);
    const sequence = useRef(0);
    const pending = useRef(0);
    const job = jobs[0];
    const enqueue = (order: RelocationOrder) => {
        const next = { ...order, key: ++sequence.current, settings: printingSettings };
        pending.current += 1;
        setNotice(null);
        setJobs((current) => [...current, next]);
    };
    const onRelocated = (response: any, warehouseId: Identifier) => {
        const data = response?.data ?? response;
        // One response represents one order, regardless of its number of rows.
        if (!hasIdentifier(data?.OrderId)) {
            setNotice({ severity: 'warning', message: 'Relocation saved, but no order ID was returned. The last printable relocation order has been kept.' });
            return;
        }
        const order = { orderId: data.OrderId, warehouseId };
        setSetting(lastOrderKey, order);
        if (autoPrint) enqueue(order);
    };
    const printLast = () => {
        if (lastOrder && pending.current === 0) enqueue(lastOrder);
    };
    const complete = (result: RelocationPrintResult) => {
        if (!job) return;
        pending.current = Math.max(0, pending.current - 1);
        setJobs((current) => current.filter((item) => item.key !== job.key));
        const outcome = result.outcome;
        if (result.error) setNotice({ severity: 'error', message: `Relocation order ${job.orderId}: ${result.error} Use Relocation Order to retry the last order.` });
        else if (outcome && outcome.method !== 'printer') setNotice({ severity: 'warning', message: `Relocation order ${job.orderId}: ${outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${outcome.reason ? ` ${outcome.reason}` : ''}` });
    };
    const feedback = <>
        {job && <RelocationOrderPrintJob key={job.key} order={job} onComplete={complete} />}
        {notice && <Box sx={{ mx: 3, mb: 2 }}><Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert></Box>}
    </>;
    return { autoPrint, setAutoPrint: (checked: boolean) => setSetting(autoPrintKey, checked), lastOrder, printing: jobs.length > 0, printLast, onRelocated, feedback };
};

export const RelocationOrderPrintControls = ({ printing, disabled = false }: { printing: ReturnType<typeof useRelocationOrderPrinting>; disabled?: boolean }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={printing.lastOrder ? 'Print last relocation order' : 'Save a relocation to print its order'} arrow><span><Button type="button" variant="outlined" startIcon={<PrintIcon />} disabled={disabled || printing.printing || !printing.lastOrder} onClick={printing.printLast}>Relocation Order</Button></span></Tooltip>
        <Tooltip title="Auto-Print Relocation Order" arrow><span><Checkbox checked={printing.autoPrint} disabled={disabled} onChange={(_, checked) => printing.setAutoPrint(checked)} inputProps={{ 'aria-label': 'Auto-Print Relocation Order' }} /></span></Tooltip>
    </Box>
);

// A fresh request for every print/reprint; receive the endpoint's PDF as bytes.
const RelocationOrderPrintJob = ({ order, onComplete }: { order: RelocationPrintJob; onComplete: (result: RelocationPrintResult) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: ['error'] });
    const startRef = useRef(() => setRequest({ ...printRelocationOrder(order.warehouseId, order.orderId), responseType: 'blob' }));
    const started = useRef(false);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => {
        mounted.current = true;
        if (!started.current) { started.current = true; startRef.current(); }
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        if (claimed.current || loading) return;
        const state: unknown = status;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        claimed.current = true;
        if (failed || state === 204 || response == null) {
            completeRef.current({ error: `Could not load the PDF (${state}).` });
            return;
        }
        printDispatchPdf(response, order.settings, 'relocationOrder', `Relocation order - ${order.orderId}`)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: error instanceof Error ? error.message : String(error) }); });
    }, [response, loading, status, order]);
    return null;
};

export const RelocateProducts = () => {
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const printing = useRelocationOrderPrinting('relocateProducts');
    const changeWarehouse = (value: Identifier) => selectWarehouse(value);

    if (warehouseId == null || warehouseId === '') return (
        <>
            {printing.feedback}
            <WarehouseSelectionPopup />
            <PageHeader {...{ title: 'Relocate Products', subTitle: 'Move products between warehouse locations', components: [{ component: <WarehouseSelector value={warehouseId} onChange={changeWarehouse} /> }] }} />
            <ContentCardsContainer><ContentCard {...{ cardId: 'ProductLocations', md: 12, cardTypes: [CardType.noHeader] }}><Typography>Select a warehouse to relocate products.</Typography></ContentCard></ContentCardsContainer>
            <Footer />
        </>
    );

    return <>{printing.feedback}<RelocationStation key={String(warehouseId)} warehouseId={warehouseId} onWarehouseChange={changeWarehouse} printing={printing} /></>;
};

const RelocationStation = ({ warehouseId, onWarehouseChange, printing }: { warehouseId: Identifier; onWarehouseChange: (value: Identifier) => void; printing: ReturnType<typeof useRelocationOrderPrinting> }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [scannerValue, setScannerValue] = useState('');
    const [selectedRowId, setSelectedRowId] = useState<Identifier | null>(null);
    const [values, setValues] = useState<RelocationValues>(emptyValues);
    const [notice, setNotice] = useState<Notice | null>(null);
    const [job, setJob] = useState<{ id: number; request: AxiosRequestConfig; productId: string; quantity: number } | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const refreshRowsRef = useRef<ProductLocationRow[] | null>(null);
    const savingRef = useRef(false);
    const requestSequence = useRef(0);
    const scannerRef = useRef<HTMLInputElement>(null);
    const quickFilterRef = useRef<HTMLInputElement>(null);
    const quantityRef = useRef<HTMLInputElement>(null);
    const focusQuantityRef = useRef(false);
    const formRef = useRef<HTMLDivElement>(null);
    const selectRow = useCallback((row: ProductLocationRow, focusQuantity = true) => {
        if (savingRef.current || refreshRowsRef.current) return;
        focusQuantityRef.current = focusQuantity;
        setSelectedRowId(row.WarehouseItemLocationId);
        setValues({ Quantity: String(Math.min(1, getProductLocationAvailable(row))), ToLocationId: '' });
        setNotice(null);
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if (focusQuantity) quantityRef.current?.focus();
    }, []);
    const [columns, tableSettings, data] = useProductLocationsTable(warehouseId, refreshKey, selectRow, job != null || refreshing);
    const locationResponse = useGetWarehouseLocationsById(String(warehouseId));
    const locations = useMemo<RelocationLocation[]>(() => (Array.isArray(locationResponse) ? locationResponse : []).filter((location) => String(location.WarehouseId) === String(warehouseId)).sort((a, b) => a.Label.localeCompare(b.Label, undefined, { numeric: true })), [locationResponse, warehouseId]);
    const searchIndex = useMemo(() => buildProductSearchIndex(data.rows), [data.rows]);
    // Always read current source stock, including after the table reloads.
    const selectedRow = data.rows.find((row) => selectedRowId != null && String(row.WarehouseItemLocationId) === String(selectedRowId)) ?? null;
    // Use all loaded rows, including locations currently hidden by grid filters.
    const sourceRows = data.rows.filter((row) => selectedRow != null && row.ProductId === selectedRow.ProductId && String(row.WarehouseId) === String(warehouseId) && Number.isInteger(Number(row.LocationId)) && Number(row.LocationId) > 0 && getProductLocationAvailable(row) > 0).sort((a, b) => String(a.Location ?? a.LocationId).localeCompare(String(b.Location ?? b.LocationId), undefined, { numeric: true }));
    const sourceRowValue = sourceRows.some((row) => String(row.WarehouseItemLocationId) === String(selectedRowId)) ? String(selectedRowId) : '';
    const destinations = locations.filter((location) => String(location.Id) !== String(selectedRow?.LocationId));
    const available = selectedRow ? getProductLocationAvailable(selectedRow) : 0;
    const saving = job != null;
    const requestStatus: unknown = data.status;
    const loadFailed = requestStatus === 'error' || requestStatus === 'aborted' || (typeof requestStatus === 'number' && requestStatus >= 400);
    const busy = saving || refreshing || tableSettings.loading || loadFailed;
    const filterModel = tableSettings.filterModel ?? { items: [] };
    const quickFilterValue = (filterModel.quickFilterValues ?? []).join(' ');
    const productFilter = filterModel.items.find((item) => item.id === productFilterId);
    const filteringSelectedProduct = selectedRow != null && String(productFilter?.value) === selectedRow.ProductId;
    const quantity = Number(values.Quantity);
    const quantityError = values.Quantity !== '' && (!Number.isFinite(quantity) || quantity <= 0 || quantity > available);
    const canSubmit = !busy && selectedRow != null && Number.isFinite(quantity) && quantity > 0 && quantity <= available && destinations.some((location) => String(location.Id) === values.ToLocationId);

    useEffect(() => { if (selectedRowId != null && focusQuantityRef.current) { quantityRef.current?.focus(); quantityRef.current?.select(); } }, [selectedRowId]);
    useEffect(() => { if (filterModel.quickFilterExcludeHiddenColumns !== false) tableSettings.apiRef.current.setFilterModel({ ...filterModel, quickFilterExcludeHiddenColumns: false }); }, [filterModel, tableSettings.apiRef]);
    // Do not allow another move using the stock snapshot from before this save.
    useEffect(() => { if (refreshing && !tableSettings.loading && (loadFailed || data.rows !== refreshRowsRef.current)) { refreshRowsRef.current = null; setRefreshing(false); } }, [refreshing, tableSettings.loading, loadFailed, data.rows]);

    const clearForm = () => {
        if (savingRef.current) return;
        setSelectedRowId(null);
        setValues(emptyValues);
        setNotice(null);
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, items: filterModel.items.filter((item) => item.id !== productFilterId) });
        scannerRef.current?.focus();
    };
    const clearQuickFilter = () => {
        if (savingRef.current) return;
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, quickFilterValues: [], quickFilterExcludeHiddenColumns: false });
        quickFilterRef.current?.focus();
    };
    const search = (value: string) => {
        if (savingRef.current) return;
        // A new scan starts a new selection; an old source must not stay active.
        setSelectedRowId(null);
        setValues(emptyValues);
        setNotice(null);
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, items: filterModel.items.filter((item) => item.id !== productFilterId), quickFilterValues: value ? [value] : [], quickFilterExcludeHiddenColumns: false });
    };
    const scan = (rawValue = scannerRef.current?.value ?? scannerValue) => {
        if (savingRef.current || busy) return;
        const value = rawValue.trim();
        setScannerValue('');
        if (!value) { scannerRef.current?.focus(); return; }
        search(value);
        const productIds = findMatchingProductIds(searchIndex, value);
        const matches = data.rows.filter((row) => productIds.has(row.ProductId));
        if (matches.length === 1 && getProductLocationAvailable(matches[0]) > 0) selectRow(matches[0], false);
        else setNotice({ severity: 'info', message: matches.length ? 'Choose the source location using Relocate on a matching row in the table.' : 'No products match this identifier.' });
        scannerRef.current?.focus();
    };
    const filterProduct = () => {
        if (savingRef.current || !selectedRow) return;
        const items = filterModel.items.filter((item) => item.id !== productFilterId);
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, items: filteringSelectedProduct ? items : [...items, { id: productFilterId, field: 'ProductId', operator: 'equals', value: selectedRow.ProductId }], logicOperator: GridLogicOperator.And, quickFilterValues: [], quickFilterExcludeHiddenColumns: false });
    };

    const changeSource = (rowId: string) => {
        if (savingRef.current || busy) return;
        const row = sourceRows.find((source) => String(source.WarehouseItemLocationId) === rowId);
        if (!row || String(selectedRowId) === rowId) return;
        focusQuantityRef.current = false;
        setSelectedRowId(row.WarehouseItemLocationId);
        // Keep the entered quantity; the new source's limit is validated below.
        setValues((current) => ({ ...current, ToLocationId: String(row.LocationId) === current.ToLocationId ? '' : current.ToLocationId }));
        setNotice(null);
    };

    const submit = () => {
        if (savingRef.current || busy) return;
        try {
            const payload = buildProductRelocation(selectedRow, values, warehouseId, locations);
            const productId = selectedRow!.ProductId;
            const request = relocateProduct(payload, warehouseId, productId);
            savingRef.current = true;
            setNotice(null);
            setJob({ id: ++requestSequence.current, request, productId, quantity: payload.Quantity });
        } catch (error) {
            setNotice({ severity: 'error', message: error instanceof Error ? error.message : 'Check the relocation fields.' });
        }
    };
    const finishSave = (succeeded: boolean, response: unknown) => {
        if (!job) return;
        savingRef.current = false;
        setJob(null);
        if (!succeeded) { setNotice({ severity: 'error', message: 'Relocation failed. Your selection and quantity have been kept.' }); return; }
        printing.onRelocated(response, warehouseId);
        setNotice({ severity: 'success', message: `Relocated ${job.quantity} of product ${job.productId}.` });
        setSelectedRowId(null);
        setValues(emptyValues);
        refreshRowsRef.current = data.rows;
        setRefreshing(true);
        setRefreshKey((value) => value + 1);
        scannerRef.current?.focus();
    };

    return (
        <>
            {job && <RelocationRequest key={job.id} request={job.request} onComplete={finishSave} />}
            <Box sx={relocationHeaderStyles}>
                <PageHeader {...{
                    title: 'Relocate Products', subTitle: 'Move one product from one location at a time',
                    components: [
                        { position: 'center', component: <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TextField inputRef={scannerRef} label="Scan product" placeholder="Product ID, manufacturer ID or EAN" value={scannerValue} onChange={(event) => setScannerValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); scan((event.target as HTMLInputElement).value); } }} disabled={saving} autoFocus autoComplete="off" sx={{ width: { xs: 180, sm: 300, md: 380 } }} /><CustomButton title="Submit scan" tooltipText="Submit scan" size="small" iconButton variant="outlined" icon={<ArrowForwardIcon />} disabled={busy} onClick={() => scan()} /></Box> },
                        { component: <TextField inputRef={quickFilterRef} label="Quick filter" placeholder="Product ID, manufacturer ID or EAN" value={quickFilterValue} onChange={(event) => search(event.target.value)} disabled={saving} autoComplete="off" InputProps={{ endAdornment: <InputAdornment position="end"><CustomButton title="Clear quick filter" tooltipText="Clear quick filter" size="small" iconButton variant="text" icon={<ClearIcon />} disabled={saving || !quickFilterValue} onClick={clearQuickFilter} /></InputAdornment> }} sx={{ width: { xs: 220, sm: 300 } }} /> },
                        { component: <WarehouseSelector value={warehouseId} disabled={saving} onChange={(value) => { if (!savingRef.current) onWarehouseChange(value); }} /> },
                        { component: <RelocationOrderPrintControls printing={printing} disabled={saving} /> },
                        ...generateTablePageButtons(tableSettings, { hideQuickSearch: true }),
                    ],
                }} />
            </Box>
            {/* {notice && <Box sx={{ mx: 3, mb: 2 }}><Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert></Box>} */}
            <ContentCardsContainer>
                <WarehouseRowEditor config={{
                    id: 'Relocate product', label: 'Relocation row', formRef, disabled: busy, onSubmit: submit, submitOnEnter: ['Quantity'],
                    summaries: [{ id: 'product', label: 'Selected product', title: selectedRow?.Name || 'Product', details: [{ id: 'ProductId', label: 'Product ID', value: selectedRow?.ProductId ?? '' }, { id: 'ManufacturerProductId', label: 'Manufacturer ID', value: selectedRow?.ManufacturerProductId ?? '' }] }],
                    actions: [<CustomButton key="filter" title={filteringSelectedProduct ? 'Show all products' : 'Filter this product'} icon={<FilterAltIcon />} variant={filteringSelectedProduct ? 'contained' : 'outlined'} disabled={!selectedRow || saving} onClick={filterProduct} />, <CustomButton key="clear" title="Clear" icon={<ClearIcon />} variant="text" disabled={saving || (!selectedRow && !productFilter)} onClick={clearForm} />],
                    layout: { columns: 'minmax(140px, 1fr) 100px minmax(180px, 1fr) 140px max-content', areas: '"source available destination quantity actions"', compactColumns: 'minmax(150px, 1fr) minmax(100px, 0.7fr) max-content', compactAreas: '"source available available" "destination quantity actions"' },
                    fields: [
                        { id: 'source', content: <CustomField options={{ name: 'FromLocation', label: 'From location', select: true }} disabled={!selectedRow || busy || !sourceRows.length} stateProps={{ state: sourceRowValue, setState: changeSource }}><MenuItem value="" disabled>Select a source location</MenuItem>{sourceRows.map(row => <MenuItem key={row.WarehouseItemLocationId} value={String(row.WarehouseItemLocationId)}>{row.Location ?? row.LocationId} ({getProductLocationAvailable(row)} available)</MenuItem>)}</CustomField> },
                        { id: 'available', content: <CustomField options={{ name: 'Available', label: 'Available', readOnly: true }} stateProps={{ state: selectedRow ? available : '' }} /> },
                        { id: 'destination', content: <CustomField options={{ name: 'ToLocationId', label: 'To location', select: true }} disabled={!selectedRow || busy || !destinations.length} stateProps={{ state: values.ToLocationId, setState: value => setValues(current => ({ ...current, ToLocationId: value })) }}><MenuItem value="" disabled>Select a location</MenuItem>{destinations.map(location => <MenuItem key={location.Id} value={String(location.Id)}>{location.Label}</MenuItem>)}</CustomField> },
                        { id: 'quantity', content: <CustomField options={{ name: 'Quantity', label: 'Quantity to move', type: 'number' }} inputRef={quantityRef} disabled={!selectedRow || busy} stateProps={{ state: values.Quantity, setState: value => setValues(current => ({ ...current, Quantity: value })) }} inputProps={{ min: 0, max: available, step: 'any', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} error={quantityError} helperText={quantityError ? 'Enter more than 0, up to ' + available + '.' : undefined} /> },
                        { id: 'actions', action: true, content: <CustomButton title="Relocate" isSubmitting={saving} disabled={!canSubmit} onClick={submit} /> },
                    ],
                }} />
                <ContentCard {...{ cardId: 'ProductLocations', md: 12, cardTypes: [CardType.noHeader], contentPadding: 0 }}><CustomDataGrid columns={columns} tableSettings={tableSettings} /></ContentCard>
            </ContentCardsContainer>
            <Footer />
        </>
    );
};

// Each attempt mounts a fresh request hook. A successful empty response still
// completes the move, and a previous attempt cannot finish a later request.
const RelocationRequest = ({ request, onComplete }: { request: AxiosRequestConfig; onComplete: (succeeded: boolean, response: unknown) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
    const startRef = useRef(() => setRequest(request));
    const startedRef = useRef(false);
    const completeRef = useRef(onComplete);
    const handledRef = useRef(false);
    completeRef.current = onComplete;
    useEffect(() => { if (!startedRef.current) { startedRef.current = true; startRef.current(); } }, []);
    useEffect(() => {
        const state: unknown = status;
        if (handledRef.current || loading) return;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        handledRef.current = true;
        completeRef.current(succeeded, response);
    }, [response, loading, status]);
    return null;
};


export default RelocateProducts;
